import { useEffect, useRef, useState } from 'react'
import { DeviceMobileCamera, X, ArrowDown } from '@phosphor-icons/react'

const DISMISSED_KEY = 'lifeodds.install-dismissed'
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    if (!raw) return false
    const ts = parseInt(raw, 10)
    return Date.now() - ts < DISMISS_TTL_MS
  } catch {
    return false
  }
}

function dismiss() {
  try { localStorage.setItem(DISMISSED_KEY, String(Date.now())) } catch { /* noop */ }
}

/** True when running as an installed PWA (standalone mode) */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as any).standalone === true)
  )
}

/** Best-effort iOS detection */
function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream
}

export default function InstallPrompt() {
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)
  const [isIos, setIsIos] = useState(false)

  useEffect(() => {
    if (isStandalone() || isDismissed()) return

    // Chrome / Android / Desktop — native install prompt available
    const handler = (e: Event) => {
      e.preventDefault()
      deferredRef.current = e as BeforeInstallPromptEvent
      setShow(true)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // iOS Safari: no beforeinstallprompt, show manual hint instead
    if (isIOS()) {
      setIsIos(true)
      setShow(true)
    }

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (!show) return null

  const handleInstall = async () => {
    if (deferredRef.current) {
      await deferredRef.current.prompt()
      const { outcome } = await deferredRef.current.userChoice
      if (outcome === 'accepted') {
        setShow(false)
        return
      }
    }
    // If no deferred prompt (iOS or prompt already used) just close
    handleDismiss()
  }

  const handleDismiss = () => {
    dismiss()
    setShow(false)
  }

  return (
    <div
      role="banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 card-brutal bg-surface p-4 flex items-start gap-3 anim-pop"
    >
      <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 border-2 border-primary/30 grid place-items-center">
        <DeviceMobileCamera size={20} weight="fill" className="text-primary-ink" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm leading-snug">Add LifeOdds to your home screen</p>
        {isIos && !deferredRef.current ? (
          <p className="text-xs text-ink-muted mt-0.5">
            Tap <ArrowDown size={11} weight="bold" className="inline mx-0.5" /> Share then "Add to Home Screen"
          </p>
        ) : (
          <p className="text-xs text-ink-muted mt-0.5">Quick access, works offline</p>
        )}
        {!isIos || deferredRef.current ? (
          <button
            onClick={handleInstall}
            className="mt-2 px-3 py-1 text-xs font-bold rounded-lg bg-primary text-white border-2 border-edge shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all"
          >
            Install
          </button>
        ) : null}
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        className="shrink-0 p-1 rounded-lg hover:bg-muted transition-colors"
      >
        <X size={16} weight="bold" className="text-ink-muted" />
      </button>
    </div>
  )
}
